'use client';

import { useEffect, useState } from 'react';
import { ORIGIN_KEY } from '@/lib/user-preferences';
import { detectGeoCountry, GEO_CACHE_KEY } from '@/hooks/useAutoCountry';
import { Flag } from '@/components/ui/Flag';
import type { BusinessImpactLevel } from '@/lib/types';

export interface SpotlightCountryData {
  name: string;
  slug: string | null;
  events: Array<{
    id: string;
    title: string;
    type: string;
    color: string;
    typeLabel: string;
  }>;
  impactLevel: BusinessImpactLevel | null;
}

interface TodaySpotlightProps {
  countryData: Record<string, SpotlightCountryData>;
}

/**
 * Hero-level spotlight for the user's detected country.
 * Placed between the date heading and stat strip in the /today hero.
 * Detects country client-side (same pattern as TodayGeoContext).
 */
export function TodaySpotlight({ countryData }: TodaySpotlightProps) {
  const [userCountry, setUserCountry] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(ORIGIN_KEY);
    if (stored) {
      setUserCountry(stored);
      return;
    }

    const cached = localStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      try {
        const { code } = JSON.parse(cached);
        if (code) { setUserCountry(code); return; }
      } catch { /* ignore */ }
    }

    detectGeoCountry().then(code => {
      if (code) setUserCountry(code);
    });
  }, []);

  const data = userCountry ? countryData[userCountry] : null;
  if (!data || data.events.length === 0) return null;

  return (
    <div className="mt-6 animate-fade-in-up rounded-2xl border-2 border-brand bg-surface-selected p-5 shadow-lg md:p-6"
         style={{ boxShadow: '0 8px 24px color-mix(in srgb, var(--brand) 15%, transparent)' }}>
      {/* Badge */}
      <span className="mb-3 inline-block rounded-full bg-brand px-3 py-1 text-xs font-semibold tracking-wide text-text-on-brand">
        Your holidays today
      </span>

      {/* Country header */}
      <div className="flex items-center gap-3">
        <Flag code={userCountry!} size={48} countryName={data.name} />
        <div>
          <h2 className="font-heading text-2xl font-bold">
            {data.slug ? (
              <a href={`/${data.slug}`} className="transition-colors duration-200 hover:text-brand">
                {data.name}
              </a>
            ) : data.name}
          </h2>
          <p className="text-sm text-text-secondary">
            {data.events.length} {data.events.length === 1 ? 'holiday' : 'holidays'} today
          </p>
        </div>
      </div>

      {/* Event list */}
      <ul className="mt-4 flex flex-col gap-1">
        {data.events.map(event => (
          <li key={event.id} className="flex items-center gap-2 rounded-md border-l-[3px] bg-surface-root/50 px-3 py-2"
              style={{ borderColor: event.color }}>
            <span className="min-w-0 flex-1 font-medium">{event.title}</span>
            <span className="shrink-0 rounded-full px-2 py-0.5 text-xs"
                  style={{
                    backgroundColor: `color-mix(in srgb, ${event.color} 12%, transparent)`,
                    color: event.color,
                  }}>
              {event.typeLabel}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
